import React, { useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSpinner, 
  faCopy,
  faWandMagicSparkles,
  faFileContract,
  faArrowRight
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-hot-toast';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useAuth } from '../../contexts/AuthContext';
import { API_ENDPOINTS } from '../../config/api';
import ReverseProposal from './ReverseProposal';
import './RFPManager.css';

const RFPManager = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('standard');
  const [briefText, setBriefText] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [results, setResults] = useState(null);
  const [emailContent, setEmailContent] = useState('');
  const [isGeneratingEmail, setIsGeneratingEmail] = useState(false);
  const [selectedCreators, setSelectedCreators] = useState([]);

  const generateFromBrief = async () => {
    if (!briefText.trim()) {
      toast.error('Please paste a brief first');
      return;
    }

    try {
      setIsGenerating(true);
      
      const { tokens } = await fetchAuthSession();
      const response = await axios.post(API_ENDPOINTS.RFP.ANALYZE, {
        rfp_data: {
          brief_text: briefText,
          brand_name: 'Extracted from brief',
          campaign_objective: 'Extracted from brief',
          brand_description: briefText,
          target_audience: 'General audience',
          budget_range: 'Not specified',
          campaign_duration: 'Not specified',
          deliverables: [
            { type: 'instagram_post', quantity: 1, description: 'Social media content' }
          ],
          requirements: briefText,
          deadline: 'Not specified',
          contact_name: 'Not specified',
          contact_email: 'Not specified',
          additional_notes: briefText
        },
        user_id: user?.username || 'anonymous'
      }, {
        headers: {
          'Authorization': `Bearer ${tokens.accessToken.toString()}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.status === 'success') {
        setResults({
          analysis: response.data.analysis,
          clients: response.data.proposed_clients
        });
        setSelectedCreators([]); // Reset selected creators when new results are generated
        setEmailContent(''); // Clear previous email content
        toast.success('Brief analyzed successfully!');
      } else {
        toast.error('Failed to analyze brief');
      }
    } catch (error) {
      console.error('Error analyzing brief:', error);
      toast.error('Error analyzing brief: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCreatorSelection = (creator, isSelected) => {
    if (isSelected) {
      setSelectedCreators(prev => [...prev, creator]);
    } else {
      setSelectedCreators(prev => prev.filter(c => c.client_id !== creator.client_id));
    }
  };

  const generateEmail = async () => {
    if (!results) {
      toast.error('Please generate creator recommendations first');
      return;
    }

    if (selectedCreators.length === 0) {
      toast.error('Please select at least one creator for the email');
      return;
    }

    try {
      setIsGeneratingEmail(true);
      
      const { tokens } = await fetchAuthSession();
      const response = await axios.post(API_ENDPOINTS.RFP.GENERATE_PROPOSAL_EMAIL, {
        rfp_data: {
          brand_name: 'Extracted from brief',
          campaign_objective: 'Extracted from brief',
          brand_description: briefText,
          target_audience: 'General audience',
          budget_range: 'Not specified',
          campaign_duration: 'Not specified',
          deliverables: [
            { type: 'instagram_post', quantity: 1, description: 'Social media content' }
          ],
          requirements: briefText,
          deadline: 'Not specified',
          contact_name: 'Marketing Team',
          contact_email: 'marketing@brand.com',
          additional_notes: briefText
        },
        analysis: results.analysis,
        proposed_clients: selectedCreators, // Only use selected creators
        user_id: user?.username || 'anonymous',
        formatting_instructions: 'Generate a professional, left-aligned email with no bold formatting, asterisks, or AI-style markdown. Use standard business email formatting with proper paragraphs and line breaks.'
      }, {
        headers: {
          'Authorization': `Bearer ${tokens.accessToken.toString()}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.status === 'success') {
        setEmailContent(response.data.email_content);
        toast.success('Email generated successfully!');
      } else {
        toast.error('Failed to generate email');
      }
    } catch (error) {
      console.error('Error generating email:', error);
      toast.error('Error generating email: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsGeneratingEmail(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  /**
   * Format email text by removing markdown and converting to clean email format
   */
  const formatEmailText = (text) => {
    if (!text) return '';
    
    return text
      // Remove markdown headers (### -> blank line)
      .replace(/^#{1,6}\s+/gm, '')
      // Remove bold markdown (**text** -> text)
      .replace(/\*\*(.*?)\*\*/g, '$1')
      // Remove italic markdown (*text* -> text)
      .replace(/\*(.*?)\*/g, '$1')
      // Clean up multiple line breaks
      .replace(/\n{3,}/g, '\n\n')
      // Clean up any remaining markdown artifacts
      .replace(/^\s*-\s*\*\*/gm, '- ')
      .replace(/\*\*:/g, ':')
      // Ensure proper spacing after sections
      .replace(/---/g, '')
      .trim();
  };

  return (
    <div className="rfp-manager-container">
      <div className="rfp-manager-header">
        <h1>Brand Management</h1>
        <p>AI-powered proposal generation and client matching</p>
      </div>

      {/* Tab Navigation */}
      <div className="rfp-tabs">
        <button 
          className={`rfp-tab ${activeTab === 'standard' ? 'active' : ''}`}
          onClick={() => setActiveTab('standard')}
        >
          <FontAwesomeIcon icon={faWandMagicSparkles} />
          Find Creators
        </button>
        <button 
          className={`rfp-tab ${activeTab === 'reverse' ? 'active' : ''}`}
          onClick={() => setActiveTab('reverse')}
        >
          <FontAwesomeIcon icon={faFileContract} />
          Find Brands
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'standard' && (
        <div className="rfp-manager-simple">
          <div className="simple-header">
            <h2>Find Creators</h2>
            <p>Paste your brief below and let AI find the perfect creators</p>
          </div>

          <div className="simple-content">
            <div className="input-section">
              <label>Paste Your Brief Here:</label>
              <textarea
                value={briefText}
                onChange={(e) => setBriefText(e.target.value)}
                placeholder="Paste your brand brief, RFP, or campaign details here..."
                rows={10}
                className="brief-input"
              />
              
              <button
                onClick={generateFromBrief}
                disabled={isGenerating || !briefText.trim()}
                className="generate-btn"
              >
                {isGenerating ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin /> Analyzing...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faWandMagicSparkles} /> Generate Results
                  </>
                )}
              </button>
            </div>

            {results && (
              <div className="results-section">
                <div className="analysis-section">
                  <h3>Campaign Analysis</h3>
                  <div className="analysis-card">
                    <h4>Campaign Summary</h4>
                    <p>{results.analysis.campaign_summary}</p>
                    
                    <h4>Key Requirements</h4>
                    <ul>
                      {results.analysis.key_requirements?.map((req, index) => (
                        <li key={index}>{req}</li>
                      ))}
                    </ul>
                    
                    <h4>Recommended Profile</h4>
                    <p>{results.analysis.recommended_profile}</p>
                  </div>
                </div>

                <div className="clients-section">
                  <div className="clients-section-header">
                    <h3>Recommended Creators ({results.clients.length})</h3>
                    <div className="selection-info">
                      {selectedCreators.length > 0 && (
                        <span className="selected-count">
                          {selectedCreators.length} selected for email
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="creator-cards-container">
                    {results.clients.map((client, index) => {
                      const isSelected = selectedCreators.some(c => c.client_id === client.client_id);
                      return (
                        <div 
                          key={index} 
                          className={`creator-card ${isSelected ? 'selected' : ''}`}
                          onClick={() => handleCreatorSelection(client, !isSelected)}
                        >
                          <div className="creator-card-header">
                            <div className="creator-card-main-info">
                              <h4 className="creator-card-name">{client.Stage_Name || `${client.First_Legal_Name} ${client.Last_Legal_Name}`}</h4>
                              <div className="creator-card-match-score">{client.match_score}% match</div>
                            </div>
                            <div className="creator-card-total-reach">
                              {client.total_followers?.toLocaleString() || 0} followers
                            </div>
                          </div>
                          
                          <div className="creator-card-stats-grid">
                            <div className="creator-stat-item">
                              <div className="creator-stat-platform">Instagram</div>
                              <div className="creator-stat-value">{client.IG_Followers?.toLocaleString() || 0}</div>
                            </div>
                            <div className="creator-stat-item">
                              <div className="creator-stat-platform">TikTok</div>
                              <div className="creator-stat-value">{client.TT_Followers?.toLocaleString() || 0}</div>
                            </div>
                          </div>
                          
                          <div className="creator-card-status-badges">
                            {client.monetized && <span className="creator-status-badge monetized">Monetized</span>}
                            {client.signed && <span className="creator-status-badge signed">Signed</span>}
                            {client.verified && <span className="creator-status-badge verified">Verified</span>}
                            {client.star_talent && <span className="creator-status-badge star">Star Talent</span>}
                          </div>
                          
                          {client.match_reasons && (
                            <div className="creator-match-reasons">
                              <div className="match-reasons-title">Why this creator:</div>
                              <ul>
                                {client.match_reasons.map((reason, idx) => (
                                  <li key={idx}>{reason}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  {selectedCreators.length > 0 && (
                    <div className="selected-creators-summary">
                      <h4>Selected Creators ({selectedCreators.length})</h4>
                      <div className="selected-creators-list">
                        {selectedCreators.map((creator, index) => (
                          <span key={index} className="selected-creator-tag">
                            {creator.Stage_Name || `${creator.First_Legal_Name} ${creator.Last_Legal_Name}`}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="email-section">
                  <button
                    onClick={generateEmail}
                    disabled={isGeneratingEmail || selectedCreators.length === 0}
                    className="generate-email-btn"
                  >
                    {isGeneratingEmail ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin /> Generating Email...
                      </>
                    ) : (
                      <>
                        Generate Proposal Email ({selectedCreators.length} creators) <FontAwesomeIcon icon={faArrowRight} />
                      </>
                    )}
                  </button>

                  {emailContent && (
                    <div className="email-result">
                      <div className="email-header">
                        <h3>Generated Email</h3>
                        <button
                          onClick={() => copyToClipboard(formatEmailText(emailContent))}
                          className="copy-btn"
                        >
                          <FontAwesomeIcon icon={faCopy} /> Copy Email
                        </button>
                      </div>
                      <div className="email-content">
                        <div className="email-text">{formatEmailText(emailContent)}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'reverse' && (
        <ReverseProposal />
      )}
    </div>
  );
};

export default RFPManager; 